import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import {
  Send, Loader2, ArrowLeft, CheckCircle2, Upload, X, Plus, Trash2, Building2, Users,
  Image as ImageIcon, MessageSquareQuote, ClipboardCheck, ShieldCheck, Sparkles,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Public vendor self-service application form.
 * Route: /vendors/apply?token=XXX
 * Backend: POST /api/vendors/apply — creates a draft vendor, notifies admins.
 */
export default function VendorApply() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [categories, setCategories] = useState([]);
  const [invite, setInvite] = useState(null);
  const [inviteError, setInviteError] = useState("");
  const [loading, setLoading] = useState(true);

  // Form state
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [description, setDescription] = useState("");
  const [website, setWebsite] = useState("");
  const [locationCity, setLocationCity] = useState("");
  const [locationState, setLocationState] = useState("");
  const [locationCountry, setLocationCountry] = useState("");
  const [services, setServices] = useState("");
  const [specialties, setSpecialties] = useState("");
  const [certifications, setCertifications] = useState("");
  const [minRunSize, setMinRunSize] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [coverImageUrl, setCoverImageUrl] = useState("");

  const [clients, setClients] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [references, setReferences] = useState([]);
  const [team, setTeam] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);

  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");

  // Honeypot — real users leave blank
  const [websiteConfirm, setWebsiteConfirm] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedSlug, setSubmittedSlug] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/vendors/apply/preview${token ? `?token=${encodeURIComponent(token)}` : ""}`)
      .then((r) => {
        if (!alive) return;
        setCategories(r.data.categories || []);
        setInvite(r.data.invite || null);
        setInviteError(r.data.invite_error || "");
        if (r.data.invite) {
          if (r.data.invite.category) setCategory(r.data.invite.category);
          if (r.data.invite.invited_name && !name) setName(r.data.invite.invited_name);
          if (r.data.invite.invited_email && !contactEmail) setContactEmail(r.data.invite.invited_email);
        }
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const validate = () => {
    if (!name.trim()) return "Company name is required";
    if (!category) return "Please pick a category";
    if (description.trim().length < 20) return "Description must be at least 20 characters";
    if (!contactName.trim()) return "Your contact name is required";
    if (!contactEmail.trim() || !contactEmail.includes("@")) return "A valid contact email is required";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        category,
        subcategory: subcategory.trim(),
        description: description.trim(),
        website: website.trim(),
        location_city: locationCity.trim(),
        location_state: locationState.trim(),
        location_country: locationCountry.trim(),
        services: services.split(",").map((s) => s.trim()).filter(Boolean),
        specialties: specialties.split(",").map((s) => s.trim()).filter(Boolean),
        certifications: certifications.split(",").map((s) => s.trim()).filter(Boolean),
        min_run_size: minRunSize.trim(),
        logo_url: logoUrl,
        cover_image_url: coverImageUrl,
        clients: clients.filter((c) => c.name?.trim()),
        gallery: gallery.filter((g) => g.image_url),
        references: references.filter((r) => r.name?.trim() && r.quote?.trim()),
        team: team.filter((t) => t.name?.trim()),
        case_studies: caseStudies.filter((c) => c.title?.trim()),
        contact_name: contactName.trim(),
        contact_email: contactEmail.trim(),
        contact_phone: contactPhone.trim(),
        token: token || undefined,
        website_confirm: websiteConfirm,  // honeypot
      };
      const { data } = await api.post("/vendors/apply", payload);
      setSubmitted(true);
      setSubmittedSlug(data.slug || "");
      toast.success("Application submitted");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-shell">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-shell flex flex-col items-center justify-center p-8">
        <div className="max-w-lg w-full text-center" data-testid="vendor-apply-success">
          <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center mx-auto">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h1 className="font-display text-4xl mt-6">You're on the list</h1>
          <p className="text-muted-foreground mt-3">
            Thanks — your listing is queued for review. An Institute admin will approve it within a few business days. Once live, it'll appear in the searchable directory for every founder-member.
          </p>
          <p className="text-[13px] text-muted-foreground mt-4">
            Reference: <code className="font-mono text-primary">{submittedSlug}</code>
          </p>
          <div className="mt-8 flex items-center justify-center gap-2">
            <Link
              to="/"
              data-testid="vendor-apply-done-home"
              className="btn-primary text-sm"
            >
              Back to CPG Institute
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-shell text-foreground">
      <header className="bg-shell-side text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white transition">
            <ArrowLeft className="w-4 h-4" /> CPG Institute
          </Link>
          <span className="text-[11px] text-white/50 uppercase tracking-[0.18em]">Vendor Directory</span>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 md:px-10 py-10">
        {invite && (
          <div className="rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 mb-8 flex items-start gap-3" data-testid="vendor-apply-invite-banner">
            <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold">You've been invited</p>
              <p className="text-[12.5px] text-muted-foreground mt-0.5">
                {invite.invited_name ? <><b>{invite.invited_name}</b> — </> : null}
                {invite.category ? <>we've pre-filled your category ({invite.category}). </> : null}
                Fill out the rest below and we'll get your listing live.
              </p>
            </div>
          </div>
        )}
        {inviteError && (
          <div className="rounded-xl border border-pink-300 bg-pink-50 px-4 py-3 mb-8 flex items-start gap-3" data-testid="vendor-apply-invite-error">
            <ShieldCheck className="w-4 h-4 text-pink-700 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-pink-900">Invite issue</p>
              <p className="text-[12.5px] text-pink-800 mt-0.5">{inviteError} You can still apply below via the public form.</p>
            </div>
          </div>
        )}

        <p className="chip-amber inline-flex items-center gap-1">
          <Building2 className="w-3 h-3" /> Vendor application
        </p>
        <h1 className="font-display text-4xl md:text-5xl mt-4">
          Get listed in the <span className="text-grad">CPG Institute directory</span>
        </h1>
        <p className="mt-4 text-muted-foreground max-w-2xl">
          Every founder-member searches this directory when they're looking for their next co-manufacturer, ingredient supplier, or agency partner. Submit your listing below — an admin will review and publish it within a few business days.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-8" data-testid="vendor-apply-form">
          {/* Basic info */}
          <FormSection title="About your company" icon={Building2}>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Company name *" testid="vendor-apply-name" value={name} onChange={setName} maxLength={140} />
              <div>
                <Label>Category *</Label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  data-testid="vendor-apply-category"
                  className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary"
                >
                  <option value="">Pick a category…</option>
                  {categories.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.label}</option>
                  ))}
                </select>
              </div>
              <Field label="Sub-category" testid="vendor-apply-subcategory" value={subcategory} onChange={setSubcategory} placeholder="e.g. Aseptic beverage, HPP tolling" maxLength={140} />
              <Field label="Website" testid="vendor-apply-website" value={website} onChange={setWebsite} placeholder="https://…" type="url" />
            </div>
            <div className="mt-4">
              <Label>Company description *</Label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                minLength={20}
                maxLength={3000}
                placeholder="Who you are, who you serve, what makes you different — this is the sales pitch a founder sees on the search results page."
                data-testid="vendor-apply-description"
                className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary resize-y"
              />
              <p className="text-[11px] text-muted-foreground mt-1">{description.length}/3000</p>
            </div>
            <div className="grid md:grid-cols-3 gap-4 mt-4">
              <Field label="City" testid="vendor-apply-city" value={locationCity} onChange={setLocationCity} />
              <Field label="State / Region" testid="vendor-apply-state" value={locationState} onChange={setLocationState} />
              <Field label="Country" testid="vendor-apply-country" value={locationCountry} onChange={setLocationCountry} />
            </div>
          </FormSection>

          {/* Services & specialties */}
          <FormSection title="Services & specialties" icon={ClipboardCheck}>
            <p className="text-[12.5px] text-muted-foreground mb-3">Separate multiple values with commas. Founders search these.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Services offered" testid="vendor-apply-services" value={services} onChange={setServices} placeholder="e.g. HPP tolling, hot fill, R&D" />
              <Field label="Specialties" testid="vendor-apply-specialties" value={specialties} onChange={setSpecialties} placeholder="e.g. Cold-pressed juice, kombucha" />
              <Field label="Certifications" testid="vendor-apply-certifications" value={certifications} onChange={setCertifications} placeholder="e.g. SQF Level 2, USDA Organic" />
              <Field label="Minimum run size" testid="vendor-apply-minrun" value={minRunSize} onChange={setMinRunSize} placeholder="e.g. 40,000 units" />
            </div>
          </FormSection>

          {/* Logo + cover */}
          <FormSection title="Logo & cover image" icon={ImageIcon}>
            <div className="grid md:grid-cols-2 gap-4">
              <ImageUploadField
                label="Company logo"
                value={logoUrl}
                onChange={setLogoUrl}
                testid="vendor-apply-logo"
                hint="Square PNG or JPG works best."
              />
              <ImageUploadField
                label="Cover image"
                value={coverImageUrl}
                onChange={setCoverImageUrl}
                testid="vendor-apply-cover"
                hint="Wide banner shown at the top of your listing."
              />
            </div>
          </FormSection>

          {/* Repeaters */}
          <RepeaterSection
            title="Clients"
            testid="clients"
            icon={Users}
            items={clients}
            setItems={setClients}
            template={{ name: "", website: "", logo_url: "" }}
            addLabel="+ Add client"
            emptyHint="Notable brands you've worked with — added as small logos on your public listing."
            renderRow={(row, update) => (
              <div className="grid md:grid-cols-[1fr_1fr_140px] gap-3">
                <input type="text" placeholder="Client name" value={row.name} onChange={(e) => update("name", e.target.value)}
                  className="rounded-lg border border-border bg-white px-3 py-2 text-sm" />
                <input type="url" placeholder="https://client.example" value={row.website} onChange={(e) => update("website", e.target.value)}
                  className="rounded-lg border border-border bg-white px-3 py-2 text-sm" />
                <ImageUploadCompact value={row.logo_url} onChange={(v) => update("logo_url", v)} label="Logo" />
              </div>
            )}
          />

          <RepeaterSection
            title="Gallery of work"
            testid="gallery"
            icon={ImageIcon}
            items={gallery}
            setItems={setGallery}
            template={{ image_url: "", caption: "" }}
            addLabel="+ Add image"
            emptyHint="Photos of your facility, past work, or product shots."
            renderRow={(row, update) => (
              <div className="grid md:grid-cols-[1fr_1fr] gap-3">
                <ImageUploadCompact value={row.image_url} onChange={(v) => update("image_url", v)} label="Image" />
                <input type="text" placeholder="Caption (optional)" value={row.caption} onChange={(e) => update("caption", e.target.value)}
                  className="rounded-lg border border-border bg-white px-3 py-2 text-sm" />
              </div>
            )}
          />

          <RepeaterSection
            title="References & testimonials"
            testid="references"
            icon={MessageSquareQuote}
            items={references}
            setItems={setReferences}
            template={{ name: "", role: "", company: "", quote: "", contact_email: "" }}
            addLabel="+ Add reference"
            emptyHint="A short quote from a founder who's worked with you."
            renderRow={(row, update) => (
              <div className="grid md:grid-cols-3 gap-3">
                <input type="text" placeholder="Reference name" value={row.name} onChange={(e) => update("name", e.target.value)}
                  className="rounded-lg border border-border bg-white px-3 py-2 text-sm" />
                <input type="text" placeholder="Role / title" value={row.role} onChange={(e) => update("role", e.target.value)}
                  className="rounded-lg border border-border bg-white px-3 py-2 text-sm" />
                <input type="text" placeholder="Company" value={row.company} onChange={(e) => update("company", e.target.value)}
                  className="rounded-lg border border-border bg-white px-3 py-2 text-sm" />
                <textarea placeholder="Quote…" value={row.quote} onChange={(e) => update("quote", e.target.value)} rows={2}
                  className="md:col-span-3 rounded-lg border border-border bg-white px-3 py-2 text-sm resize-y" />
              </div>
            )}
          />

          <RepeaterSection
            title="Team / contacts"
            testid="team"
            icon={Users}
            items={team}
            setItems={setTeam}
            template={{ name: "", title: "", email: "", linkedin_url: "", photo_url: "" }}
            addLabel="+ Add teammate"
            emptyHint="Key contacts a founder should reach out to."
            renderRow={(row, update) => (
              <div className="grid md:grid-cols-[80px_1fr_1fr] gap-3">
                <ImageUploadCompact value={row.photo_url} onChange={(v) => update("photo_url", v)} label="Photo" square />
                <div className="grid gap-2">
                  <input type="text" placeholder="Name" value={row.name} onChange={(e) => update("name", e.target.value)}
                    className="rounded-lg border border-border bg-white px-3 py-2 text-sm" />
                  <input type="text" placeholder="Title" value={row.title} onChange={(e) => update("title", e.target.value)}
                    className="rounded-lg border border-border bg-white px-3 py-2 text-sm" />
                </div>
                <div className="grid gap-2">
                  <input type="email" placeholder="Email (optional)" value={row.email} onChange={(e) => update("email", e.target.value)}
                    className="rounded-lg border border-border bg-white px-3 py-2 text-sm" />
                  <input type="url" placeholder="LinkedIn URL (optional)" value={row.linkedin_url} onChange={(e) => update("linkedin_url", e.target.value)}
                    className="rounded-lg border border-border bg-white px-3 py-2 text-sm" />
                </div>
              </div>
            )}
          />

          <RepeaterSection
            title="Case studies"
            testid="case-studies"
            icon={ClipboardCheck}
            items={caseStudies}
            setItems={setCaseStudies}
            template={{ title: "", summary: "", url: "" }}
            addLabel="+ Add case study"
            emptyHint="Short recap of a client project — a URL to a full write-up is optional."
            renderRow={(row, update) => (
              <div className="grid gap-2">
                <div className="grid md:grid-cols-[2fr_1fr] gap-2">
                  <input type="text" placeholder="Title" value={row.title} onChange={(e) => update("title", e.target.value)}
                    className="rounded-lg border border-border bg-white px-3 py-2 text-sm" />
                  <input type="url" placeholder="URL (optional)" value={row.url} onChange={(e) => update("url", e.target.value)}
                    className="rounded-lg border border-border bg-white px-3 py-2 text-sm" />
                </div>
                <textarea placeholder="Short summary…" value={row.summary} onChange={(e) => update("summary", e.target.value)} rows={2}
                  className="rounded-lg border border-border bg-white px-3 py-2 text-sm resize-y" />
              </div>
            )}
          />

          {/* Contact */}
          <FormSection title="Your contact details" icon={Send}>
            <p className="text-[12.5px] text-muted-foreground mb-3">
              Kept private. We'll use this only to email you when your listing is approved and if a member requests an intro.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <Field label="Your name *" testid="vendor-apply-contact-name" value={contactName} onChange={setContactName} />
              <Field label="Email *" testid="vendor-apply-contact-email" value={contactEmail} onChange={setContactEmail} type="email" />
              <Field label="Phone (optional)" testid="vendor-apply-contact-phone" value={contactPhone} onChange={setContactPhone} placeholder="+1 555 123 4567" />
            </div>
          </FormSection>

          {/* Honeypot — hidden from real users */}
          <div className="hidden" aria-hidden="true">
            <label>Website confirm (leave blank)
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={websiteConfirm}
                onChange={(e) => setWebsiteConfirm(e.target.value)}
              />
            </label>
          </div>

          <div className="pt-4 flex items-center justify-end gap-3 sticky bottom-0 bg-shell/90 backdrop-blur border-t border-shell-border py-4 -mx-6 md:-mx-10 px-6 md:px-10">
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-primary transition"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={submitting}
              data-testid="vendor-apply-submit"
              className="btn-primary text-sm inline-flex items-center gap-1.5"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {submitting ? "Submitting…" : "Submit for review"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Reusable form primitives ────────────────────────────────────────── */
function FormSection({ title, icon: Icon, children }) {
  return (
    <section className="surface p-6" data-testid={`vendor-apply-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <h2 className="font-display text-xl mb-4 inline-flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-primary" />} {title}
      </h2>
      {children}
    </section>
  );
}

function Label({ children }) {
  return <span className="text-xs font-semibold">{children}</span>;
}

function Field({ label, value, onChange, testid, type = "text", placeholder, maxLength }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        data-testid={testid}
        className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary"
      />
    </div>
  );
}

function RepeaterSection({ title, testid, icon: Icon, items, setItems, template, addLabel, emptyHint, renderRow }) {
  const add = () => setItems((prev) => [...prev, { ...template }]);
  const update = (idx, key, val) => setItems((prev) => prev.map((r, i) => i === idx ? { ...r, [key]: val } : r));
  const remove = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  return (
    <section className="surface p-6" data-testid={`vendor-apply-section-${testid}`}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-xl inline-flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-primary" />} {title}
        </h2>
        <button
          type="button"
          onClick={add}
          data-testid={`vendor-apply-${testid}-add`}
          className="inline-flex items-center gap-1 rounded-full border border-shell-border px-3 py-1.5 text-xs font-semibold hover:border-primary/40 transition"
        >
          <Plus className="w-3.5 h-3.5" /> {addLabel.replace(/^\+\s*/, "")}
        </button>
      </div>
      {items.length === 0 ? (
        <p className="text-[13px] text-muted-foreground italic">{emptyHint}</p>
      ) : (
        <ul className="space-y-3">
          {items.map((row, idx) => (
            <li key={idx} className="rounded-xl border border-shell-border bg-shell-muted/30 p-3 relative" data-testid={`vendor-apply-${testid}-row-${idx}`}>
              {renderRow(row, (k, v) => update(idx, k, v))}
              <button
                type="button"
                onClick={() => remove(idx)}
                data-testid={`vendor-apply-${testid}-remove-${idx}`}
                title="Remove"
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border border-shell-border grid place-items-center text-muted-foreground hover:text-rose-600 hover:border-rose-200 transition"
              >
                <X className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ── Image upload widgets (backed by /api/public/vendor-uploads) ────── */
function useImageUpload() {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return null;
    if (!file.type.startsWith("image/")) { toast.error("Only image files"); return null; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5 MB"); return null; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/public/vendor-uploads`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed");
      return data.url;
    } catch (err) {
      toast.error(formatError(err));
      return null;
    } finally { setUploading(false); }
  };
  return { upload, uploading };
}

function ImageUploadField({ label, value, onChange, testid, hint }) {
  const { upload, uploading } = useImageUpload();
  const handle = async (e) => {
    const f = e.target.files?.[0];
    const url = await upload(f);
    if (url) onChange(url);
    e.target.value = "";
  };
  return (
    <div>
      <Label>{label}</Label>
      {value ? (
        <div className="mt-1 relative w-full h-40 rounded-lg border border-shell-border overflow-hidden bg-shell-muted/40" data-testid={`${testid}-preview`}>
          <img src={value} alt="" className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={() => onChange("")}
            data-testid={`${testid}-remove`}
            className="absolute top-2 right-2 rounded-full bg-black/60 text-white w-7 h-7 grid place-items-center hover:bg-black/80"
            title="Remove"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <label className="mt-1 grid place-items-center w-full h-40 rounded-lg border-2 border-dashed border-shell-border bg-white text-muted-foreground cursor-pointer hover:border-primary/40 transition text-sm">
          {uploading ? (
            <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</span>
          ) : (
            <span className="inline-flex items-center gap-2"><Upload className="w-4 h-4" /> Click to upload</span>
          )}
          <input type="file" className="hidden" accept="image/*" onChange={handle} data-testid={`${testid}-input`} />
        </label>
      )}
      {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

function ImageUploadCompact({ value, onChange, label, square }) {
  const { upload, uploading } = useImageUpload();
  const handle = async (e) => {
    const url = await upload(e.target.files?.[0]);
    if (url) onChange(url);
    e.target.value = "";
  };
  const box = square ? "w-16 h-16" : "w-full h-20";
  return value ? (
    <div className={`relative ${box} rounded-lg border border-shell-border overflow-hidden bg-shell-muted/40`}>
      <img src={value} alt="" className="w-full h-full object-cover" />
      <button type="button" onClick={() => onChange("")} title="Remove"
        className="absolute -top-1 -right-1 rounded-full bg-black/60 text-white w-5 h-5 grid place-items-center hover:bg-black/80">
        <X className="w-2.5 h-2.5" />
      </button>
    </div>
  ) : (
    <label className={`grid place-items-center ${box} rounded-lg border border-dashed border-shell-border bg-white text-muted-foreground cursor-pointer hover:border-primary/40 transition text-[11px]`}>
      {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Upload className="w-3.5 h-3.5" /> {label}</>}
      <input type="file" className="hidden" accept="image/*" onChange={handle} />
    </label>
  );
}
